/* eslint-disable consistent-default-export-name/default-export-match-filename */
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import DashboardTable from './DashboardTable';
import DigestLogsPanel from './DigestLogsPanel';
import {
  PROJECT_DASHBOARD_TYPE__EAPP_DASHBOARD_V2,
  DASHBOARD_PERSPECTIVE__EAPPS,
  DASHBOARD_PERSPECTIVE__PATIENTS,
  DASHBOARD_PERSPECTIVE__RESPONSES,
  DASHBOARD_PERSPECTIVE__ACTIVITIES,
  DASHBOARD_PERSPECTIVE__PARTICIPATIONS,
} from '../../../../common/constants';
import { default as RecipientSelect } from '../../../../common/selectors/Recipient';
import createSelectors from '../../../../common/selectors/createSelectors';
import { selectAllProjectMilestones } from '../../selectors';
import EAPP from '../../../../common/models/EAPP';
import usePagination from '../../../../utils/usePagination';
import { apiZedocSearchEAPPs } from '../../../../common/api/zedoc';
import { registerType } from '../../dashboards';
import Stack from '../../../../common/components/primitives/Stack';
import Tabs from '../../../../components/layouts/Tabs';
import ConnectedFilters from './ConnectedFilters';
import store from '../../store';
import cleanFilters from '../../../../utils/cleanFilters';
import usePermission from '../../../../utils/usePermission';
import ProjectSelect from '../../../../common/selectors/Project';
import { PROJECT_VIEW_CUSTOM_REPORT_LOGS } from '../../../../common/permissions';

const EAPPSelect = createSelectors(EAPP);

const TAB_TRIGGERED = 'triggered';
const TAB_DIGEST_LOGS = 'digest-logs';

const DashboardEAppV2 = ({
  projectId,
  answersSheetId,
  recipientId,
  participationId,
  activityId,
  dashboardId,
  hideColumns,
}) => {
  const [activeTab, setActiveTab] = useState(TAB_TRIGGERED);
  const [digestTotal, setDigestTotal] = useState(null);
  const onDigestTotalChange = useCallback((n) => setDigestTotal(n), []);

  const rawFilters = useSelector(
    store.get(`dashboards.${dashboardId}.filters`),
  );
  const filters = useMemo(
    () => cleanFilters(rawFilters, { anyOf: true }),
    [rawFilters],
  );

  const {
    // ready: subscriptionsReady,
    items: dataSource,
    totalItems: triggeredTotal,
    getPaginationProps,
  } = usePagination({
    // Back to the first page whenever the filters change, see usePagination.
    resetPageKey: filters,
    selector: EAPPSelect,
    mapSelector: (selector) => {
      return selector.lookup({
        from: RecipientSelect.all(),
        as: 'recipient',
        foreignKey: '_id',
        key: (doc, _, byRecipientId) => {
          return (
            byRecipientId[doc.recipientId] && byRecipientId[doc.recipientId][0]
          );
        },
      });
    },
    getSubscription: (currentPage, resultsPerPage) => {
      const params = {
        controlId: '$meta.id',
        pageIndex: currentPage - 1,
        resultsPerPage,
        filters,
      };

      if (projectId) {
        params.projectId = projectId;
      }

      if (answersSheetId) {
        params.answersSheetId = answersSheetId;
      }

      if (recipientId) {
        params.recipientId = recipientId;
      }

      if (participationId) {
        params.participationId = participationId;
      }

      if (activityId) {
        params.activityId = activityId;
      }

      return apiZedocSearchEAPPs.withParams(params);
    },
  });

  useDDPSubscription({
    name: 'Users.projectMembers',
    params: [projectId],
  });

  const projectMilestones = useSelector(selectAllProjectMilestones);

  const project = useSelector(ProjectSelect.one().whereIdEquals(projectId));
  const canViewDigestLogs = usePermission([PROJECT_VIEW_CUSTOM_REPORT_LOGS], {
    relativeTo: project ? project.getDomains() : [],
  });

  const tabs = useMemo(
    () => [
      {
        value: TAB_TRIGGERED,
        label: `Triggered alerts${
          typeof triggeredTotal === 'number' ? ` (${triggeredTotal})` : ''
        }`,
        content: (
          <Stack>
            <ConnectedFilters
              storeKey={`dashboards.${dashboardId}`}
              projectId={projectId}
              recipientId={recipientId}
              participationId={participationId}
              activityId={activityId}
              answersSheetId={answersSheetId}
              dashboardId={dashboardId}
              projectMilestones={projectMilestones}
              hideColumns={hideColumns}
            />
            <DashboardTable
              dataSource={dataSource}
              projectMilestones={projectMilestones}
              pagination={getPaginationProps()}
              hideColumns={hideColumns}
            />
          </Stack>
        ),
      },
      ...(canViewDigestLogs
        ? [
            {
              value: TAB_DIGEST_LOGS,
              label: `Digest logs${
                typeof digestTotal === 'number' ? ` (${digestTotal})` : ''
              }`,
              content: (
                <DigestLogsPanel
                  projectId={projectId}
                  dashboardId={dashboardId}
                  onTotalChange={onDigestTotalChange}
                />
              ),
            },
          ]
        : []),
    ],
    [
      triggeredTotal,
      digestTotal,
      canViewDigestLogs,
      dashboardId,
      projectId,
      recipientId,
      participationId,
      activityId,
      answersSheetId,
      projectMilestones,
      hideColumns,
      dataSource,
      getPaginationProps,
      onDigestTotalChange,
    ],
  );

  return (
    <Stack>
      <div>
        <h2 className="text-lg font-medium m-0">Alerts</h2>
        <p className="text-sm text-gray-500 m-0 mt-1">
          Review triggered alerts and digest delivery activity.
        </p>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabs={tabs}
        hasDivider
        space={4}
      />
    </Stack>
  );
};

DashboardEAppV2.propTypes = {
  projectId: PropTypes.string,
  answersSheetId: PropTypes.string,
  recipientId: PropTypes.string,
  participationId: PropTypes.string,
  activityId: PropTypes.string,
  dashboardId: PropTypes.string,
  hideColumns: PropTypes.arrayOf(PropTypes.string),
};

DashboardEAppV2.defaultProps = {
  projectId: null,
  answersSheetId: null,
  recipientId: null,
  participationId: null,
  activityId: null,
  dashboardId: null,
  hideColumns: null,
};

registerType(PROJECT_DASHBOARD_TYPE__EAPP_DASHBOARD_V2, {
  component: DashboardEAppV2,
  perspectives: [
    {
      genericView: true,
      perspective: DASHBOARD_PERSPECTIVE__EAPPS,
      specializations: [
        DASHBOARD_PERSPECTIVE__PATIENTS,
        DASHBOARD_PERSPECTIVE__PARTICIPATIONS,
        DASHBOARD_PERSPECTIVE__ACTIVITIES,
        DASHBOARD_PERSPECTIVE__RESPONSES,
      ],
    },
  ],
});

export default DashboardEAppV2;
